import { useCallback, useEffect, useState } from "react";
import api, { API, fileUrl, uploadBill, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { ExpenseCharts } from "@/components/ExpenseCharts";
import { PortfolioOverview } from "@/components/PortfolioOverview";
import {
  Receipt, Lock, DollarSign, TrendingUp, Percent, PiggyBank, Trash2, Plus,
  Paperclip, FileText, Building2, Settings2, Save, Download, LayoutGrid,
} from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const money2 = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TYPES = ["gas", "water", "electric", "sewer", "trash", "internet", "insurance", "hoa", "property_tax", "repairs", "landscaping", "pest_control", "management", "other"];
const TYPE_LABEL = (t) => (t || "other").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Stat = ({ icon: Icon, label, value, sub, tone = "forest", testid }) => (
  <div data-testid={testid} className="bg-white border border-line rounded-xl p-4">
    <div className="flex items-center gap-2 text-mute"><Icon className={`h-4 w-4 text-${tone}`} /><span className="text-xs font-medium">{label}</span></div>
    <p className="text-2xl font-heading font-semibold text-ink mt-1.5">{value}</p>
    {sub && <p className="text-xs text-mute mt-0.5">{sub}</p>}
  </div>
);

const EMPTY = { type: "electric", amount: "", date_paid: new Date().toISOString().slice(0, 10), reference: "", note: "", recurring: false, bill_path: null, bill_name: null };

export const ExpensesTab = ({ properties = [], isPro, onUpgrade }) => {
  const [locked, setLocked] = useState(!isPro);
  const [loading, setLoading] = useState(true);
  const [pid, setPid] = useState(properties[0]?.id || "");
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showFin, setShowFin] = useState(false);
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [fin, setFin] = useState({ purchase_price: "", monthly_mortgage: "", other_fixed_monthly: "" });

  useEffect(() => { if (!pid && properties[0]?.id) setPid(properties[0].id); }, [properties, pid]);

  const load = useCallback(async () => {
    if (!pid) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get(`/properties/${pid}/expenses?year=${year}`);
      setData(d); setLocked(false);
      setFin({
        purchase_price: d.financials.purchase_price ?? "",
        monthly_mortgage: d.financials.monthly_mortgage ?? "",
        other_fixed_monthly: d.financials.other_fixed_monthly ?? "",
      });
    } catch (e) {
      if (e.response?.status === 403) setLocked(true);
    } finally { setLoading(false); }
  }, [pid, year]);

  useEffect(() => { load(); }, [load]);

  const onBill = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadBill(file);
      setForm((f) => ({ ...f, bill_path: res.path, bill_name: res.name }));
      toast.success("Bill attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload the bill"); }
    finally { setUploading(false); }
  };

  const addExpense = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount"); return; }
    setBusy(true);
    try {
      await api.post(`/properties/${pid}/expenses`, { ...form, amount: Number(form.amount) });
      setForm(EMPTY);
      toast.success("Expense recorded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the expense"); }
    finally { setBusy(false); }
  };

  const removeExpense = async (eid) => {
    if (!window.confirm("Delete this expense (and its attached bill)?")) return;
    try { await api.delete(`/expenses/${eid}`); toast.success("Expense deleted"); load(); }
    catch { toast.error("Could not delete the expense"); }
  };

  const saveFin = async () => {
    setBusy(true);
    try {
      await api.put(`/properties/${pid}/financials`, {
        purchase_price: fin.purchase_price === "" ? null : Number(fin.purchase_price),
        monthly_mortgage: fin.monthly_mortgage === "" ? null : Number(fin.monthly_mortgage),
        other_fixed_monthly: fin.other_fixed_monthly === "" ? null : Number(fin.other_fixed_monthly),
      });
      toast.success("Property financials saved");
      load();
    } catch { toast.error("Could not save financials"); }
    finally { setBusy(false); }
  };

  if (locked) {
    return (
      <div data-testid="expenses-locked" className="max-w-xl mx-auto bg-white border border-line rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center mx-auto"><Lock className="h-6 w-6 text-forest" /></div>
        <h2 className="text-xl font-heading font-medium text-ink mt-4">Expense Tracking is a Pro feature</h2>
        <p className="text-sm text-mute mt-2">Log utilities and operating costs per property, attach bills for records, and see monthly totals, net operating income, cash flow, cap rate and ROI — the numbers behind each property's health.</p>
        <ul className="text-sm text-ink text-left max-w-xs mx-auto mt-4 space-y-1.5">
          <li className="flex items-center gap-2"><Receipt className="h-4 w-4 text-forest" /> Track gas, water, electric, repairs & more</li>
          <li className="flex items-center gap-2"><Paperclip className="h-4 w-4 text-forest" /> Attach PDF or photo bills</li>
          <li className="flex items-center gap-2"><Percent className="h-4 w-4 text-forest" /> Automatic cap rate & ROI</li>
        </ul>
        <button data-testid="expenses-upgrade-btn" onClick={onUpgrade} className="mt-5 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors">Upgrade to Pro</button>
      </div>
    );
  }

  if (!properties.length) {
    return <div data-testid="expenses-no-props" className="text-sm text-mute py-10 text-center">Add a property first to start tracking expenses.</div>;
  }

  const s = data?.summary;

  return (
    <div className="space-y-6" data-testid="expenses-content">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-forest" />
          <select data-testid="expense-property" value={pid} onChange={(e) => setPid(e.target.value)} className="border border-line rounded-lg px-3 py-2 text-sm bg-white max-w-[280px]">
            {properties.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <select data-testid="expense-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-line rounded-lg px-3 py-2 text-sm bg-white">
          {[0, 1, 2].map((n) => { const y = new Date().getFullYear() - n; return <option key={y} value={y}>{y}</option>; })}
        </select>
        <a data-testid="export-csv-btn" href={`${API}/properties/${pid}/expenses/export?year=${year}`} className="inline-flex items-center gap-1.5 text-sm border border-line rounded-lg px-3 py-2 text-ink hover:bg-bone">
          <Download className="h-4 w-4" /> Export CSV
        </a>
        <button data-testid="toggle-portfolio" onClick={() => setShowPortfolio((v) => !v)} className={`inline-flex items-center gap-1.5 text-sm border rounded-lg px-3 py-2 ${showPortfolio ? "border-forest bg-forest/5 text-forest" : "border-line text-ink hover:bg-bone"}`}>
          <LayoutGrid className="h-4 w-4" /> Portfolio
        </button>
        <button data-testid="toggle-financials" onClick={() => setShowFin((v) => !v)} className="ml-auto inline-flex items-center gap-1.5 text-sm border border-line rounded-lg px-3 py-2 text-ink hover:bg-bone">
          <Settings2 className="h-4 w-4" /> Property financials
        </button>
      </div>

      {showPortfolio && <PortfolioOverview year={year} />}

      {/* Financials editor */}
      {showFin && (
        <div data-testid="financials-editor" className="bg-white border border-line rounded-xl p-4 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-mute">Purchase price</label>
            <input data-testid="fin-purchase" type="number" value={fin.purchase_price} onChange={(e) => setFin({ ...fin, purchase_price: e.target.value })} placeholder="e.g. 250000" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-mute">Monthly mortgage</label>
            <input data-testid="fin-mortgage" type="number" value={fin.monthly_mortgage} onChange={(e) => setFin({ ...fin, monthly_mortgage: e.target.value })} placeholder="e.g. 1200" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-medium text-mute">Other fixed / month</label>
            <input data-testid="fin-fixed" type="number" value={fin.other_fixed_monthly} onChange={(e) => setFin({ ...fin, other_fixed_monthly: e.target.value })} placeholder="e.g. 100" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
          </div>
          <button data-testid="save-financials" onClick={saveFin} disabled={busy} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60"><Save className="h-4 w-4" /> Save</button>
        </div>
      )}

      {loading && <div data-testid="expenses-loading" className="text-sm text-mute py-6 text-center">Loading…</div>}

      {!loading && s && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat testid="stat-total-expenses" icon={Receipt} label={`Expenses ${year}`} value={money2(s.total_expenses)} sub={`${data.expenses.filter((e) => (e.date_paid || "").startsWith(String(year))).length} entries`} tone="terracotta" />
            <Stat testid="stat-rent-income" icon={DollarSign} label={`Rent income ${year}`} value={money2(s.rent_income)} sub="Collected" />
            <Stat testid="stat-noi" icon={TrendingUp} label="Net operating income" value={money2(s.noi)} sub="Income − expenses" />
            <Stat testid="stat-cashflow" icon={PiggyBank} label="Cash flow" value={money2(s.cash_flow)} sub="After mortgage & fixed" tone={s.cash_flow < 0 ? "terracotta" : "forest"} />
            <Stat testid="stat-cap-rate" icon={Percent} label="Cap rate" value={s.cap_rate == null ? "—" : `${s.cap_rate}%`} sub={s.cap_rate == null ? "Add purchase price" : "Annualized"} />
            <Stat testid="stat-roi" icon={Percent} label="Annual ROI" value={s.annual_roi == null ? "—" : `${s.annual_roi}%`} sub={s.annual_roi == null ? "Add purchase price" : "Cash flow ÷ price"} />
          </div>

          <ExpenseCharts summary={s} year={year} />

          {/* Add expense */}
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-forest" /> Record an expense</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-mute">Type</label>
                <select data-testid="exp-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm bg-white">
                  {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL(t)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-mute">Amount paid</label>
                <input data-testid="exp-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-mute">Date paid</label>
                <input data-testid="exp-date" type="date" value={form.date_paid} onChange={(e) => setForm({ ...form, date_paid: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-mute">Check / confirmation #</label>
                <input data-testid="exp-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. 1042 / CONF123" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs font-medium text-mute">Note (optional)</label>
                <input data-testid="exp-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. Winter bill" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex items-end gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer border border-line rounded-lg px-3 py-2 hover:bg-bone">
                  <Paperclip className="h-4 w-4 text-forest" /> {uploading ? "Uploading…" : form.bill_name ? "Change bill" : "Attach bill"}
                  <input data-testid="exp-bill" type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => onBill(e.target.files?.[0])} />
                </label>
                {form.bill_name && <span className="text-xs text-mute truncate max-w-[140px]">{form.bill_name}</span>}
                <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer ml-auto">
                  <input data-testid="exp-recurring" type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} /> Recurring monthly
                </label>
              </div>
            </div>
            <button data-testid="add-expense-btn" onClick={addExpense} disabled={busy || uploading} className="mt-4 inline-flex items-center gap-1.5 bg-forest text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60"><Plus className="h-4 w-4" /> Add expense</button>
          </div>

          {/* Expense list */}
          <div className="bg-white border border-line rounded-xl p-4 overflow-hidden">
            <p className="text-sm font-medium text-ink mb-2">All expenses</p>
            {data.expenses.length === 0 ? <p className="text-xs text-mute">No expenses recorded yet.</p> : (
              <div className="divide-y divide-line" data-testid="expense-list">
                {data.expenses.map((e) => (
                  <div key={e.id} data-testid={`expense-row-${e.id}`} className="flex items-center gap-2 sm:gap-3 py-2.5 text-sm">
                    <span className="w-16 sm:w-24 shrink-0 text-mute text-xs">{formatDate(e.date_paid)}</span>
                    <span className="shrink-0 font-medium text-ink text-xs sm:text-sm">{TYPE_LABEL(e.type)}{e.recurring && <span className="ml-1 text-[9px] text-forest bg-forest/10 rounded-full px-1.5 py-0.5 uppercase">rec</span>}</span>
                    <span className="flex-1 min-w-0 truncate text-mute text-xs">{e.reference && `#${e.reference}`}{e.reference && e.note ? " · " : ""}{e.note}</span>
                    {e.bill_path && <a data-testid={`bill-${e.id}`} href={fileUrl(e.bill_path)} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 text-xs text-forest hover:underline"><FileText className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Bill</span></a>}
                    <span className="shrink-0 text-right font-medium text-ink tabular-nums text-xs sm:text-sm">{money2(e.amount)}</span>
                    <button data-testid={`delete-expense-${e.id}`} onClick={() => removeExpense(e.id)} className="shrink-0 text-mute hover:text-terracotta"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
